import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  Box,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Pagination,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tab,
  Tabs,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Drawer
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ShoppingCart as ShoppingCartIcon,
  Refresh as RefreshIcon,
  ArrowUpward,
  ArrowDownward,
  Clear as ClearIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import ImageCarousel from './ImageCarousel';

const ShopProductGrid = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [searchCode, setSearchCode] = useState('');
  const [searchName, setSearchName] = useState('');
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [cart, setCart] = useState([]);
  
  // Department and Category state
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [brands, setBrands] = useState(['all']);
  const [showPublicPrice, setShowPublicPrice] = useState(false);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [departmentCategories, setDepartmentCategories] = useState({});
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [sortTabValue, setSortTabValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Stati per i filtri iniziali completi
  const [initialDepartments, setInitialDepartments] = useState([]);
  const [initialCategoriesByDept, setInitialCategoriesByDept] = useState({});

  // Stato per tracciare se i filtri sono stati caricati
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  
  // Stato per mostrare lo stato di aggiornamento dei listini
  const [updating, setUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateStatus, setUpdateStatus] = useState('');
  
  // Caricamento iniziale dei dati
  useEffect(() => {
    loadInitialFilters();
    loadUserProfile();
  }, []); // Solo all'avvio
  
  // Funzione per caricare il profilo utente
  const loadUserProfile = async () => {
    try {
      console.log('👤 Caricamento profilo utente...');
      const response = await api.get('/auth/me');
      if (response.data && response.data.role) {
        setUserRole(response.data.role);
        console.log('✅ Ruolo utente caricato:', response.data.role);
      }
    } catch (error) {
      console.error('❌ Errore nel caricamento del profilo utente:', error);
    }
  };

  // Funzione per caricare tutti i filtri iniziali
  const loadInitialFilters = async () => {
    try {
      console.log('🔄 Caricamento filtri iniziali...');
      
      // Chiamata per ottenere tutti i prodotti senza filtri
      const allProductsRes = await api.get('/b2b-products', {
        params: {
          limit: 100000,
          page: 1,
          showOnlyAvailable: false
        }
      });
      
      if (allProductsRes.data && allProductsRes.data.products) {
        const products = allProductsRes.data.products;
        
        // Estrai tutti i reparti unici
        const allDepartments = [...new Set(products
          .filter(p => p.department)
          .map(p => p.department))]
          .sort((a, b) => a.localeCompare(b));
        
        console.log('🏬 Reparti iniziali trovati:', allDepartments.length);
        setInitialDepartments(allDepartments);
        setDepartments(allDepartments);
        
        // Estrai tutte le categorie uniche per reparto
        const categoriesByDept = {};
        products.forEach(product => {
          if (product.department && product.category) {
            if (!categoriesByDept[product.department]) {
              categoriesByDept[product.department] = new Set();
            }
            categoriesByDept[product.department].add(product.category);
          }
        });
        
        // Converti i Set in array ordinati
        Object.keys(categoriesByDept).forEach(dept => {
          categoriesByDept[dept] = [...categoriesByDept[dept]].sort((a, b) => a.localeCompare(b));
        });
        
        console.log('📑 Categorie iniziali trovate per', Object.keys(categoriesByDept).length, 'reparti');
        setInitialCategoriesByDept(categoriesByDept);
        setDepartmentCategories(categoriesByDept);
        
        // Estrai tutti i brand unici
        const allBrands = [...new Set(products
          .filter(p => p.brand)
          .map(p => p.brand))]
          .sort((a, b) => a.localeCompare(b));
        
        console.log('🏷️ Brand iniziali trovati:', allBrands.length);
        setBrands(['all', ...allBrands]); // Aggiorna anche l'elenco dei brand visualizzati
        
        // Imposta lo stato filtersLoaded a true per indicare che i filtri sono pronti
        setFiltersLoaded(true);
        console.log('✅ Filtri iniziali caricati con successo');
      }
    } catch (err) {
      console.error('Error loading initial filters:', err);
      // Anche in caso di errore, imposta filtersLoaded a true per evitare blocchi
      setFiltersLoaded(true);
    }
  };
  
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const normalizeString = (str) => {
        if (!str) return '';
        let normalized = str.toLowerCase();
        normalized = normalized.replace(/\./g, ',');
        return normalized;
      };
      
      const normalizedSearchCode = normalizeString(searchCode);
      const normalizedSearchName = normalizeString(searchName);
      
      // Verifica se qualsiasi filtro è attivo
      const isAnyFilterActive = normalizedSearchCode || 
                                normalizedSearchName || 
                                (selectedDepartment && selectedDepartment !== 'all') || 
                                (selectedCategory && selectedCategory !== 'all') || 
                                brandSearchTerm ||
                                showOnlyAvailable;
      
      console.log('🔍 Filtri attivi:', isAnyFilterActive ? 'SI' : 'NO', {
        code: normalizedSearchCode,
        name: normalizedSearchName,
        dept: selectedDepartment,
        cat: selectedCategory,
        brand: brandSearchTerm,
        available: showOnlyAvailable
      });
      
      // Se ci sono filtri attivi, otteniamo i reparti e le categorie filtrate
      if (isAnyFilterActive) {
        try {
          const filtersRes = await api.get('/b2b-products/filtered-departments', {
            params: {
              searchCode: normalizedSearchCode,
              searchName: normalizedSearchName,
              showOnlyAvailable,
              department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
              category: selectedCategory !== 'all' ? selectedCategory : undefined,
              brand: brandSearchTerm && brandSearchTerm !== 'all' ? brandSearchTerm : undefined
            }
          });

          if (filtersRes.data) {
            console.log('🏬 Reparti filtrati ricevuti:', filtersRes.data.departments.length);
            setDepartments(filtersRes.data.departments);
            setDepartmentCategories(filtersRes.data.categoriesByDepartment);
          }
        } catch (err) {
          console.error('Errore nel recupero dei reparti filtrati:', err);
        }
      } else {
        // Se nessun filtro è attivo, ripristina i valori iniziali
        console.log('🔄 Ripristino filtri iniziali (nessun filtro attivo)');
        setDepartments(initialDepartments);
        setDepartmentCategories(initialCategoriesByDept);
      }
      
      // Chiamata per i prodotti filtrati
      const res = await api.get('/b2b-products', {
        params: {
          page,
          limit,
          searchCode: normalizedSearchCode,
          searchName: normalizedSearchName,
          department: selectedDepartment === 'all' ? '' : selectedDepartment,
          category: selectedCategory === 'all' ? '' : selectedCategory,
          brand: brandSearchTerm,
          sortBy,
          sortOrder,
          showOnlyAvailable
        }
      });
      
      if (res.data) {
        console.log('📦 Prodotti ricevuti:', res.data.products.length, 'di', res.data.total);
        setProducts(res.data.products);
        setTotalProducts(res.data.total);
        setTotalPages(Math.ceil(res.data.total / limit));
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchCode, searchName, selectedDepartment, selectedCategory, brandSearchTerm, sortBy, sortOrder, showOnlyAvailable, initialDepartments, initialCategoriesByDept]);

  // Fetch products when filters change, ma solo quando i filtri sono stati caricati
  useEffect(() => {
    if (!filtersLoaded) {
      console.log('⏳ In attesa del caricamento dei filtri iniziali...');
      return;
    }
    console.log('🔄 Avvio fetchProducts dopo filtro cambiato');
    fetchProducts();
  }, [page, searchCode, searchName, selectedDepartment, selectedCategory, brandSearchTerm, sortBy, sortOrder, showOnlyAvailable, filtersLoaded, fetchProducts]);
  
  // Update categories when department changes, considering active filters
  useEffect(() => {
    if (!filtersLoaded) return; // Non eseguire se i filtri non sono ancora caricati
    
    console.log('🔄 Aggiornamento categorie per reparto:', selectedDepartment);
    
    if (selectedDepartment && selectedDepartment !== 'all') {
      // Se c'è un reparto selezionato, usa le categorie di quel reparto dai prodotti filtrati
      if (departmentCategories[selectedDepartment]) {
        const categoriesForDept = departmentCategories[selectedDepartment];
        console.log('📋 Categorie per', selectedDepartment, ':', categoriesForDept.length);
        setCategories(categoriesForDept);
      } else {
        // Se non ci sono categorie per questo reparto nei prodotti filtrati,
        // usa le categorie iniziali se disponibili
        if (initialCategoriesByDept[selectedDepartment]) {
          console.log('📋 Usando categorie iniziali per', selectedDepartment);
          setCategories(initialCategoriesByDept[selectedDepartment]);
        } else {
          console.log('📋 Nessuna categoria trovata per', selectedDepartment);
          setCategories([]);
        }
      }
      // NON resettiamo più la categoria qui, per permettere all'utente di selezionare una categoria specifica
      // setSelectedCategory('all'); // Rimuoviamo questa linea
    } else {
      // Se nessun reparto selezionato (all), mostra tutte le categorie disponibili
      // basate sui prodotti filtrati correnti
      const allFilteredCategories = [];
      Object.values(departmentCategories).forEach(cats => {
        allFilteredCategories.push(...cats);
      });
      
      // Rimuovi duplicati e ordina
      const uniqueCategories = [...new Set(allFilteredCategories)]
        .sort((a, b) => a.localeCompare(b));
      
      console.log('📋 Tutte le categorie disponibili:', uniqueCategories.length);
      setCategories(uniqueCategories);
    }
  }, [selectedDepartment, departmentCategories, initialCategoriesByDept, filtersLoaded]);

  const handleUpdateCatalog = async () => {
    setUpdating(true);
    setUpdateProgress(0);
    setUpdateStatus('Inizio aggiornamento...');
    
    try {
      setUpdateStatus('Avvio processo di aggiornamento...');
      setUpdateProgress(20);
      
      // Chiamata all'API per avviare l'aggiornamento del catalogo
      await api.post('/b2b-products/update-catalog');
      
      setUpdateStatus('Processo avviato in background...');
      setUpdateProgress(60);
      
      // Simula il progresso per dare feedback all'utente
      setTimeout(() => {
        setUpdateStatus('Elaborazione in corso...');
        setUpdateProgress(80);
      }, 2000);
      
      setTimeout(() => {
        setUpdateStatus('Aggiornamento completato');
        setUpdateProgress(100);
        
        // Ricarica i prodotti dopo un breve ritardo
        setTimeout(async () => {
          await fetchProducts();
          setSnackbarMessage('Aggiornamento catalogo avviato con successo. I prodotti verranno aggiornati in background.');
          setSnackbarOpen(true);
        }, 1000);
      }, 4000);
      
    } catch (err) {
      console.error('Errore nell\'aggiornamento dei listini:', err);
      setSnackbarMessage('Errore nell\'aggiornamento dei listini: ' + (err.response?.data?.error || err.message));
      setSnackbarOpen(true);
    } finally {
      // Reset dell'interfaccia dopo 5 secondi
      setTimeout(() => {
        setUpdating(false);
        setUpdateProgress(0);
        setUpdateStatus('');
      }, 5000);
    }
  };

  const handleSearchCodeChange = (e) => {
    setSearchCode(e.target.value);
    setPage(1);
  };

  const handleSearchNameChange = (e) => {
    setSearchName(e.target.value);
    setPage(1);
  };

  const handleDepartmentChange = (e) => {
    const newDepartment = e.target.value;
    const oldDepartment = selectedDepartment;
    console.log(`🔄 Cambio reparto da ${oldDepartment} a ${newDepartment}`);
    
    setSelectedDepartment(newDepartment);
    
    // Reset categoria solo quando l'utente cambia reparto
    if (newDepartment !== oldDepartment) {
      console.log('🔄 Reset categoria per cambio reparto');
      setSelectedCategory('all');
    }
    
    setPage(1);
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    console.log(`🔄 Cambio categoria da ${selectedCategory} a ${newCategory}`);
    setSelectedCategory(newCategory);
    setPage(1);
  };



  const handleResetFilters = () => {
    console.log('🔄 Reset di tutti i filtri');
    setSearchCode('');
    setSearchName('');
    setSelectedDepartment('all');
    setSelectedCategory('all');
    setBrandSearchTerm('');
    setSortBy('name');
    setSortOrder('asc');
    setSortTabValue(0);
    setShowOnlyAvailable(false);
    setShowPublicPrice(false);
    setPage(1);
    
    // Ripristina i filtri iniziali
    setDepartments(initialDepartments);
    setDepartmentCategories(initialCategoriesByDept);
    
    // Dopo il reset, aggiorna anche i prodotti
    setTimeout(() => {
      fetchProducts();
    }, 0);
  };
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when changing page
    window.scrollTo(0, 0);
  };

  const handleOpenProductDialog = (product) => {
    console.log('Prodotto selezionato:', product);
    setSelectedProduct(product);
    // Imposta la quantità minima quando si apre il dialog
    setQuantity(product.minQty || 1);
    setOpenProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setOpenProductDialog(false);
    setSelectedProduct(null);
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    if (selectedProduct) {
      const minQty = parseInt(selectedProduct.minQty) || 1;
      
      // Se il valore è minore del minimo, imposta il minimo
      if (value < minQty) {
        setQuantity(minQty);
      } else {
        // Arrotonda al multiplo superiore della quantità minima
        const remainder = value % minQty;
        if (remainder === 0) {
          // Se è già un multiplo esatto, usa il valore così com'è
          setQuantity(value);
        } else {
          // Altrimenti arrotonda al multiplo superiore
          const adjustedValue = value + (minQty - remainder);
          setQuantity(adjustedValue);
        }
      }
    }
  };

  const handleAddToCart = (product) => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (!companyId) {
        console.error('ShopProductGrid - ID azienda mancante, impossibile aggiungere al carrello');
        return;
      }

      // Check if product is already in cart
      const existingProductIndex = cart.findIndex(item => item.id === product.id);
      let newCart;

      if (existingProductIndex >= 0) {
        // Update quantity if product already in cart
        newCart = [...cart];
        newCart[existingProductIndex].quantity += quantity;
      } else {
        // Add new product to cart
        newCart = [...cart, {
          id: product.id,
          code: product.code,
          name: product.name,
          price: product.purchasePrice,
          purchasePrice: product.purchasePrice,
          quantity: quantity,
          minStock: product.minStock || 1,
          image: product.image,
          brand: product.brand
        }];
      }

      // Save to localStorage
      localStorage.setItem(`shopCart_${companyId}`, JSON.stringify(newCart));
      setCart(newCart);
      console.log('🛒 ShopProductGrid - Prodotto aggiunto al carrello:', product.name);
      
      // Show success message
      setSnackbarMessage('Prodotto aggiunto al carrello');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('ShopProductGrid - Errore nell\'aggiunta al carrello:', err);
      setSnackbarMessage('Errore nell\'aggiunta al carrello');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };





  // Funzione per gestire il cambio del tab di ordinamento
  const handleSortTabChange = (event, newValue) => {
    setSortTabValue(newValue);
    let newSortBy = 'name';
    let newSortOrder = 'asc';
    
    switch(newValue) {
      case 0: // Rilevanza
        newSortBy = 'relevance';
        newSortOrder = 'desc';
        break;
      case 1: // Prezzo crescente
        newSortBy = 'price';
        newSortOrder = 'asc';
        break;
      case 2: // Prezzo decrescente
        newSortBy = 'price';
        newSortOrder = 'desc';
        break;
      default:
        break;
    }
    
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1); // Reset page when changing sort
  };

  // Load cart data when component mounts
  useEffect(() => {
    const loadCart = () => {
      try {
        const companyId = localStorage.getItem('companyId');
        console.log('🛒 ShopProductGrid - Tentativo di caricamento carrello per azienda:', companyId);
        
        if (!companyId) {
          console.error('ShopProductGrid - ID azienda mancante, impossibile caricare il carrello');
          return;
        }
        
        const savedCart = localStorage.getItem(`shopCart_${companyId}`);
        console.log('🛒 ShopProductGrid - Carrello salvato trovato:', savedCart ? 'Sì' : 'No');
        
        if (!savedCart) {
          console.log('🛒 ShopProductGrid - Nessun carrello trovato nel localStorage');
          setCart([]);
          return;
        }

        // Parse and validate cart data
        const cartData = JSON.parse(savedCart);
        if (!Array.isArray(cartData)) {
          console.error('ShopProductGrid - Formato carrello non valido');
          setCart([]);
          return;
        }

        console.log('🛒 ShopProductGrid - Carrello caricato con successo:', cartData.length, 'articoli');
        setCart(cartData);
      } catch (err) {
        console.error('ShopProductGrid - Errore nel caricamento del carrello:', err);
        setCart([]);
      }
    };

    loadCart();
  }, []);

  return (
    <>
      {console.log(" Products da visualizzare:", products)}
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      overflow: 'hidden',
      maxWidth: '98%',
      margin: '0 auto',
      p: 0 
    }}>
      {/* Sidebar con filtri - nascosta su mobile */}
      <Paper 
        sx={{ 
          width: 286,
          p: 1,
          height: '100%', 
          overflowY: 'auto',
          position: 'sticky',
          top: 0,
          borderRight: 1,
          borderColor: 'divider',
          display: { xs: 'none', md: 'block' } // Nascosta su mobile
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6" gutterBottom>
            Filtri
          </Typography>
          
          <TextField
            size="small"
            placeholder="Cerca per codice..."
            value={searchCode}
            onChange={handleSearchCodeChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchCode && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchCode('');
                      setPage(1);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            fullWidth
          />
          
          <TextField
            size="small"
            placeholder="Cerca per descrizione..."
            value={searchName}
            onChange={handleSearchNameChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchName && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchName('');
                      setPage(1);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            fullWidth
          />
          
          <FormControl size="small" fullWidth>
              <InputLabel>Reparto</InputLabel>
            <Select
              value={selectedDepartment}
              onChange={handleDepartmentChange}
                label="Reparto"
                endAdornment={selectedDepartment !== 'all' && (
                  <IconButton
                    size="small"
                    sx={{ mr: 4 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDepartment('all');
                      setSelectedCategory('all');
                      setPage(1);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
            >
              <MenuItem value="all">
                {(searchName || searchCode) ? 'Tutti i reparti con risultati' : 'Tutti i reparti'}
              </MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" fullWidth>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              label="Categoria"
                endAdornment={selectedCategory !== 'all' && (
                  <IconButton
                    size="small"
                    sx={{ mr: 4 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory('all');
                      setPage(1);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
            >
              <MenuItem value="all">Tutte</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            
            <Autocomplete
              freeSolo
              size="small"
              options={brands.filter(b => b !== 'all')}
              value={brandSearchTerm}
              onChange={(event, newValue) => {
                setBrandSearchTerm(newValue || '');
                setPage(1);
              }}
              onInputChange={(event, newInputValue) => {
                setBrandSearchTerm(newInputValue);
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cerca marca"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          
          <FormControl size="small" fullWidth>
            <InputLabel>Prezzi</InputLabel>
            <Select
              value={showPublicPrice ? 'public' : 'purchase'}
              onChange={(e) => setShowPublicPrice(e.target.value === 'public')}
              label="Prezzi"
            >
              <MenuItem value="public">Prezzi al Pubblico</MenuItem>
              <MenuItem value="purchase">Prezzi di Acquisto</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyAvailable}
                onChange={(e) => {
                  setShowOnlyAvailable(e.target.checked);
                  setPage(1); // Reset page quando cambia il filtro
                }}
                color="primary"
              />
            }
            label="Solo disponibili"
          />
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            fullWidth
          >
            Reset
          </Button>

          {userRole === 'superadmin' && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleUpdateCatalog}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Aggiorna Listini
            </Button>
          )}
        </Box>
      </Paper>

      {/* Contenuto principale */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
          {/* Header con navigazione e ordinamento */}
        <Paper 
          sx={{ 
            p: 1,
            borderBottom: 1, 
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Pulsante filtri mobile */}
              {isMobile && (
                <IconButton 
                  onClick={() => setDrawerOpen(true)}
                  color="primary"
                >
                  <FilterListIcon />
                </IconButton>
              )}
              <Typography variant="h6">
                Catalogo Prodotti
              </Typography>
            </Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={sortTabValue} 
                  onChange={handleSortTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  <Tab 
                    icon={<SortIcon />} 
                    iconPosition={isMobile ? "top" : "start"}
                    label={isMobile ? "Rilevanza" : "Per rilevanza"}
                  />
                  <Tab 
                    icon={<ArrowUpward />} 
                    iconPosition={isMobile ? "top" : "start"}
                    label={isMobile ? "↗ Prezzo" : "Prezzo crescente"}
                  />
                  <Tab 
                    icon={<ArrowDownward />} 
                    iconPosition={isMobile ? "top" : "start"}
                    label={isMobile ? "↘ Prezzo" : "Prezzo decrescente"}
                  />
                </Tabs>
              </Box>
          </Box>
        </Paper>

        {/* Contenuto scrollabile */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {/* Pagination info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Mostrando {products.length} prodotti {totalProducts > 0 ? `(${(page - 1) * limit + 1}-${Math.min(page * limit, totalProducts)} di ${totalProducts})` : ''}
            </Typography>
          </Box>

          {/* Loading indicator */}
          {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
              <Typography color="error">{error}</Typography>
          ) : (
            <>
              {/* Products grid */}
                {products.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6">Nessun prodotto trovato</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Prova a modificare i filtri di ricerca
                  </Typography>
                </Paper>
              ) : (
                  <Grid container spacing={2} sx={{ p: 2 }}>
                    {console.log("📦 Rendering products grid con", products.length, "prodotti")}
                    {products
                      .filter(product => !showOnlyAvailable || product.available > 0)
                      .map((product) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                      <Card 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                            height: '100%',
                          '&:hover': {
                              boxShadow: 6,
                              cursor: 'pointer'
                          }
                        }}
                        onClick={() => handleOpenProductDialog(product)}
                      >
                        <ImageCarousel 
                          imageUrls={product.image}
                          productName={product.name}
                          height={160}
                          objectFit="contain"
                        />
                          <CardContent>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              {product.code || ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1, height: '40px', overflow: 'hidden' }}>
                              {product.name || ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                              Brand: {product.brand || ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                              Categoria: {product.category || ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                              Qtà min.: {product.minQty || 1}
                          </Typography>
                          <Typography variant="h6" color="primary" gutterBottom>
                            € {showPublicPrice 
                                ? (typeof product.sellingPrice === 'number' ? product.sellingPrice.toFixed(2) : '0.00')
                                : (typeof product.purchasePrice === 'number' ? product.purchasePrice.toFixed(2) : '0.00')
                              }
                          </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color={product.available > 0 ? 'success.main' : 'error.main'}>
                                {product.available > 0 ? 'Disponibile' : 'Non disponibile'}
                          </Typography>
                              {product.available > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                                    Qtà Minima acquisto:
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary" fontWeight="bold">
                                    {product.minQty || 1}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              
              {/* Pagination controls */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handlePageChange} 
                    color="primary" 
                    size="large"
                    showFirstButton 
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Product Detail Dialog */}
      <Dialog
        open={openProductDialog}
        onClose={handleCloseProductDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedProduct && `Codice: ${selectedProduct.code || ''}`}
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ImageCarousel 
                  imageUrls={selectedProduct.image}
                  productName={selectedProduct.name || ''}
                  height={400}
                  objectFit="contain"
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body1" color="textPrimary" sx={{ mt: 1, mb: 2 }}>
                        {selectedProduct.name || ''}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                          Brand: {selectedProduct.brand || ''}
                      </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Categoria: {selectedProduct.category || ''}
                      </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Quantità minima di acquisto: {selectedProduct.minQty || 1}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {showPublicPrice ? (
                        <>
                          <Typography variant="subtitle2" color="textSecondary">
                            Prezzo al pubblico (IVA inclusa):
                          </Typography>
                          <Typography variant="h4" color="primary">
                            €{typeof selectedProduct.sellingPrice === 'number' 
                                ? selectedProduct.sellingPrice.toFixed(2) 
                                : '0.00'}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="subtitle2" color="textSecondary">
                                Prezzo di acquisto:
                              </Typography>
                              <Typography variant="h4" color="primary">
                                €{typeof selectedProduct.purchasePrice === 'number' 
                                    ? selectedProduct.purchasePrice.toFixed(2) 
                                    : '0.00'}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="subtitle2" color="textSecondary">
                                Prezzo di vendita:
                              </Typography>
                              <Typography variant="h4" color="success.main">
                                €{typeof selectedProduct.sellingPrice === 'number' 
                                    ? selectedProduct.sellingPrice.toFixed(2) 
                                    : '0.00'}
                              </Typography>
                            </Box>
                          </Box>
                        </>
                      )}
                        <Typography variant="body2" color={parseInt(selectedProduct.available) > 0 ? 'success.main' : 'error.main'} sx={{ mt: 1 }}>
                          {parseInt(selectedProduct.available) > 0 ? 'Disponibile' : 'Non disponibile'}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 3 }}>
                      <TextField
                        type="number"
                          label={`Quantità (multipli di ${selectedProduct.minQty || 1})`}
                        value={quantity}
                        onChange={handleQuantityChange}
                        InputProps={{ 
                          inputProps: { 
                              min: selectedProduct.minQty || 1,
                              step: selectedProduct.minQty || 1
                          }
                        }}
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => handleAddToCart(selectedProduct)}
                        disabled={parseInt(selectedProduct.available) <= 0}
                    >
                      Aggiungi al Carrello
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Dialog di aggiornamento */}
      <Dialog open={updating} onClose={() => {}}>
        <DialogTitle>Aggiornamento Listini</DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={updateProgress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {updateStatus}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Filtri
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <ClearIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Cerca per codice..."
              value={searchCode}
              onChange={handleSearchCodeChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchCode && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchCode('');
                        setPage(1);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              fullWidth
            />
            
            <TextField
              size="small"
              placeholder="Cerca per descrizione..."
              value={searchName}
              onChange={handleSearchNameChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchName && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchName('');
                        setPage(1);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              fullWidth
            />
            
            <FormControl size="small" fullWidth>
              <InputLabel>Reparto</InputLabel>
              <Select
                value={selectedDepartment}
                onChange={handleDepartmentChange}
                label="Reparto"
              >
                <MenuItem value="all">
                  {(searchName || searchCode) ? 'Tutti i reparti con risultati' : 'Tutti i reparti'}
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                label="Categoria"
              >
                <MenuItem value="all">Tutte</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              
            <Autocomplete
              freeSolo
              size="small"
              options={brands.filter(b => b !== 'all')}
              value={brandSearchTerm}
              onChange={(event, newValue) => {
                setBrandSearchTerm(newValue || '');
                setPage(1);
              }}
              onInputChange={(event, newInputValue) => {
                setBrandSearchTerm(newInputValue);
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cerca marca"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
            
            <FormControl size="small" fullWidth>
              <InputLabel>Prezzi</InputLabel>
              <Select
                value={showPublicPrice ? 'public' : 'purchase'}
                onChange={(e) => setShowPublicPrice(e.target.value === 'public')}
                label="Prezzi"
              >
                <MenuItem value="public">Prezzi al Pubblico</MenuItem>
                <MenuItem value="purchase">Prezzi di Acquisto</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyAvailable}
                  onChange={(e) => {
                    setShowOnlyAvailable(e.target.checked);
                    setPage(1);
                  }}
                  color="primary"
                />
              }
              label="Solo disponibili"
            />
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleResetFilters}
              fullWidth
            >
              Reset Filtri
            </Button>
            
            <Button
              variant="contained"
              onClick={() => setDrawerOpen(false)}
              fullWidth
            >
              Applica Filtri
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
    </>
  );
};

export default ShopProductGrid;